import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Sparkles, Send, Mail, MessageCircle, AlertTriangle, ExternalLink, SkipForward, StopCircle, CheckCircle2, Smartphone, Loader2 } from "lucide-react";
import { publicUrl } from "@/lib/publicUrl";

const GOALS = [
    "Proposta di valutazione gratuita dell'immobile",
    "Richiesta aggiornamento sullo stato dell'immobile",
    "Invito a fissare un appuntamento conoscitivo",
];
const BUYER_GOALS = [
    "Proporre un immobile compatibile con le esigenze del cliente",
    "Invito a fissare una visita all'immobile individuato",
    "Aggiornamento: nuova opportunità in linea con la ricerca",
];
const TONES = ["professionale", "amichevole", "più breve e diretto"];

export default function CampaignComposer({ contacts, buyers, onClose, onDone }) {
    const isBuyers = Array.isArray(buyers) && buyers.length > 0;
    const targets = isBuyers ? buyers : (contacts || []);
    const [step, setStep] = useState(1);
    const [channel, setChannel] = useState("email");
    const [waMode, setWaMode] = useState("semi_manual");
    const [showRisk, setShowRisk] = useState(false);
    const [riskAck, setRiskAck] = useState(false);
    const [waStatus, setWaStatus] = useState(null);
    const [goal, setGoal] = useState(isBuyers ? BUYER_GOALS[0] : GOALS[0]);
    const [tone, setTone] = useState(TONES[0]);
    const [variants, setVariants] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [aiVariations, setAiVariations] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [campaign, setCampaign] = useState(null);

    const eligible = isBuyers
        ? buyers.filter((b) => (channel === "email" ? b.lead.email : b.lead.phone))
        : contacts.filter((c) => !c.unsubscribed && c.marketing_consent && (channel === "email" ? c.email : c.phone));
    const blocked = targets.length - eligible.length;

    const generate = async (baseText) => {
        setGenerating(true); setError(null);
        try {
            const r = await api.post("/crm/contacts/ai-generate", { channel, goal, tone, base_text: baseText || undefined, audience: isBuyers ? "buyers" : "owners" });
            setVariants(r.data.variants || []);
        } catch (e) { setError(e?.response?.data?.detail || "Errore generazione AI"); }
        finally { setGenerating(false); }
    };

    const pickVariant = (v) => { setSubject(v.subject || subject); setBody(v.body || ""); };

    useEffect(() => {
        if (channel !== "whatsapp") return;
        let alive = true;
        const load = () => api.get("/crm/whatsapp/status").then((r) => { if (alive) setWaStatus(r.data); }).catch(() => {});
        load();
        const it = setInterval(load, 4000);
        return () => { alive = false; clearInterval(it); };
    }, [channel]);

    const send = async () => {
        setSending(true); setError(null);
        try {
            const r = await api.post("/crm/campaigns", {
                channel, subject, body,
                audience: isBuyers ? "buyers" : "owners",
                contact_ids: isBuyers ? undefined : contacts.map((c) => c.id),
                buyer_targets: isBuyers ? buyers.map((b) => ({ lead_id: b.lead.lead_id, property_id: b.property.property_id })) : undefined,
                whatsapp_mode: channel === "whatsapp" ? waMode : undefined,
                ai_variations: channel === "whatsapp" ? aiVariations : false,
                risk_acknowledged: channel === "whatsapp" && waMode === "automatic" ? riskAck : undefined,
            });
            setCampaign(r.data);
            if (channel === "whatsapp") setStep(4);
            else setStep(5);
        } catch (e) { setError(e?.response?.data?.detail || "Errore creazione campagna"); }
        finally { setSending(false); }
    };

    const chooseAuto = () => {
        setRiskAck(false);
        setShowRisk(true);  // banner re-shown every time automatic is chosen
    };
    const confirmAuto = () => { setWaMode("automatic"); setShowRisk(false); setRiskAck(true); };
    const waReady = waStatus?.state === "ready";

    return (
        <div className="fixed inset-0 z-[10060] bg-black/70 flex items-center justify-center p-4" onClick={step === 4 ? undefined : onClose}>
            <div data-testid="campaign-composer" className="bg-ink-950 border border-white/15 max-w-3xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <h4 className="font-serif text-xl text-ink-50">{isBuyers ? "Proposta immobili agli acquirenti" : "Nuova comunicazione"} · {targets.length} {isBuyers ? "acquirenti" : "contatti"}</h4>
                    {step !== 4 && <button data-testid="composer-close" onClick={onClose} className="text-ink-400 hover:text-ink-50">✕</button>}
                </div>
                {error && <div className="mb-4 p-3 border border-red-700/40 bg-red-900/15 text-xs text-red-200">{error}</div>}

                {step === 1 && (
                    <>
                        <p className="text-xs text-ink-400 mb-4">Scegli il canale di invio:</p>
                        <div className="grid grid-cols-2 gap-3 mb-5">
                            <button data-testid="channel-email" onClick={() => setChannel("email")} className={`p-5 border text-left ${channel === "email" ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/30"}`}>
                                <Mail size={18} className="text-blue-300 mb-2" /><div className="text-sm text-ink-50">Email</div>
                                <div className="text-[10px] text-ink-500 mt-1">Invio automatico via Resend, con link di disiscrizione e tracciamento esiti</div>
                            </button>
                            <button data-testid="channel-whatsapp" onClick={() => setChannel("whatsapp")} className={`p-5 border text-left ${channel === "whatsapp" ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/30"}`}>
                                <MessageCircle size={18} className="text-green-300 mb-2" />
                                <div className="text-sm text-ink-50 flex items-center gap-2">WhatsApp <span className="text-[8px] uppercase px-1.5 py-0.5 border border-amber-600/50 text-amber-300">Non ufficiale</span></div>
                                <div className="text-[10px] text-ink-500 mt-1">Via WhatsApp Web con messaggi personalizzati e variazioni AI anti-spam</div>
                            </button>
                        </div>
                        {channel === "whatsapp" && (
                            <div className="mb-5 space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs text-ink-400">Modalità di invio WhatsApp:</p>
                                    <span data-testid="wa-conn-indicator" className={`text-[10px] flex items-center gap-1 ${waReady ? "text-green-300" : "text-amber-300"}`}>
                                        <Smartphone size={11} /> {waReady ? `Collegato${waStatus?.phone ? " +" + waStatus.phone : ""}` : "Non collegato"}
                                    </span>
                                </div>
                                <label data-testid="wa-mode-semi" className={`flex items-start gap-3 p-3 border cursor-pointer ${waMode === "semi_manual" ? "border-gold bg-gold/5" : "border-white/10"}`}>
                                    <input type="radio" checked={waMode === "semi_manual"} onChange={() => setWaMode("semi_manual")} className="mt-0.5" />
                                    <div><div className="text-xs text-ink-50">Semi-Manuale <span className="text-[9px] text-green-300 ml-1">✓ consigliata</span></div>
                                        <div className="text-[10px] text-ink-500">Il CRM apre in sequenza ogni chat con il messaggio già pronto: sei tu a premere Invia. Più sicura per il tuo numero.</div></div>
                                </label>
                                <label data-testid="wa-mode-auto" className={`flex items-start gap-3 p-3 border cursor-pointer ${waMode === "automatic" ? "border-gold bg-gold/5" : "border-white/10"}`} onClick={(e) => { e.preventDefault(); chooseAuto(); }}>
                                    <input type="radio" checked={waMode === "automatic"} readOnly className="mt-0.5" />
                                    <div><div className="text-xs text-ink-50">Automatica <span className="text-[8px] uppercase px-1.5 py-0.5 border border-amber-600/50 text-amber-300 ml-1">Rischio ban</span></div>
                                        <div className="text-[10px] text-ink-500">Invio in sequenza senza intervento, con ritardo random 5-15s. Richiede connessione WhatsApp Web e accettazione dei rischi (riproposta ad ogni attivazione).</div></div>
                                </label>
                                {waMode === "automatic" && !waReady && (
                                    <div data-testid="wa-not-connected-warning" className="p-2.5 border border-amber-600/40 bg-amber-900/10 text-[11px] text-amber-200">
                                        ⚠️ WhatsApp Web non è collegato. Chiudi e usa il pulsante <b>WhatsApp</b> in alto per scansionare il QR, poi torna qui.
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="mb-5 p-3 border border-white/10 bg-ink-900/40 text-[11px] text-ink-300">
                            <b className="text-ink-50">{eligible.length}</b> {isBuyers ? "acquirenti riceveranno la proposta" : "contatti riceveranno il messaggio"}.
                            {blocked > 0 && <span className="text-orange-300"> {blocked} verranno esclusi automaticamente ({isBuyers ? `senza ${channel === "email" ? "email" : "numero"}` : `senza consenso marketing GDPR, disiscritti o senza ${channel === "email" ? "email" : "numero"}`}).</span>}
                        </div>
                        <button data-testid="composer-next-1" onClick={() => setStep(2)} disabled={eligible.length === 0 || (channel === "whatsapp" && waMode === "automatic" && !waReady)} className="cta-gold px-5 py-2.5 text-xs disabled:opacity-40">Avanti → Componi il messaggio</button>
                    </>
                )}

                {step === 2 && (
                    <>
                        <div className="mb-4 p-4 border border-purple-700/30 bg-purple-900/5">
                            <div className="flex items-center gap-2 text-purple-300 text-[11px] uppercase tracking-wider mb-3"><Sparkles size={13} /> Genera con AI</div>
                            <div className="flex flex-wrap gap-2 mb-2">
                                {GOALS.map((g) => <button key={g} onClick={() => setGoal(g)} className={`px-2.5 py-1.5 text-[10px] border ${goal === g ? "border-purple-400 text-purple-200 bg-purple-900/20" : "border-white/10 text-ink-400"}`}>{g}</button>)}
                            </div>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {TONES.map((t) => <button key={t} onClick={() => setTone(t)} className={`px-2.5 py-1 text-[10px] border ${tone === t ? "border-gold text-gold" : "border-white/10 text-ink-400"}`}>tono {t}</button>)}
                            </div>
                            <div className="flex gap-2">
                                <button data-testid="ai-generate-btn" onClick={() => generate()} disabled={generating} className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs disabled:opacity-50">{generating ? "Generazione..." : "Genera 3 varianti"}</button>
                                {body && <button data-testid="ai-rewrite-btn" onClick={() => generate(body)} disabled={generating} className="px-4 py-2 border border-purple-500/40 text-purple-200 text-xs disabled:opacity-50">Riscrivi il testo attuale</button>}
                            </div>
                            {variants.length > 0 && (
                                <div className="mt-3 space-y-2">
                                    {variants.map((v, i) => (
                                        <button key={`${(v.subject || "")}-${(v.body || "").slice(0, 40)}`} data-testid={`ai-variant-${i}`} onClick={() => pickVariant(v)} className="block w-full text-left p-3 border border-white/10 hover:border-purple-400/50 text-[11px] text-ink-300">
                                            {v.subject && <div className="text-ink-50 mb-1">📩 {v.subject}</div>}
                                            <div className="line-clamp-3 whitespace-pre-wrap">{v.body}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {channel === "email" && (
                            <input data-testid="composer-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto email"
                                className="w-full mb-3 bg-transparent border border-white/15 px-3 py-2.5 text-sm text-ink-50 focus:border-gold outline-none" />
                        )}
                        <textarea data-testid="composer-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8}
                            placeholder={isBuyers ? "Scrivi il messaggio... Usa {nome}, {immobile_proposto_indirizzo}, {immobile_proposto_prezzo} per personalizzare ogni invio." : "Scrivi il messaggio... Usa {nome} e {indirizzo_immobile} per personalizzare ogni invio."}
                            className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-ink-50 focus:border-gold outline-none mb-2" />
                        <div className="text-[10px] text-ink-500 mb-3">Variabili disponibili: {isBuyers ? "{nome} {immobile_proposto_indirizzo} {immobile_proposto_prezzo} {immobile_proposto_descrizione_breve} {link_scheda_immobile}" : "{nome} {cognome} {indirizzo_immobile} {citta}"} — sostituite automaticamente per ogni {isBuyers ? "destinatario con i dati dell'immobile abbinato" : "contatto"}.{isBuyers && channel === "email" ? " La foto dell'immobile e il link alla scheda vengono aggiunti automaticamente all'email." : ""}</div>
                        {channel === "whatsapp" && (
                            <label className="flex items-center gap-2 text-[11px] text-ink-300 mb-4 cursor-pointer">
                                <input data-testid="ai-variations-check" type="checkbox" checked={aiVariations} onChange={(e) => setAiVariations(e.target.checked)} />
                                Genera leggere variazioni AI del messaggio per ogni contatto (riduce il rischio spam)
                            </label>
                        )}
                        <div className="flex gap-2">
                            <button onClick={() => setStep(1)} className="cta-outline px-4 py-2.5 text-xs">← Indietro</button>
                            <button data-testid="composer-preview-btn" onClick={() => setStep(3)} disabled={!body.trim() || (channel === "email" && !subject.trim())} className="cta-gold px-5 py-2.5 text-xs disabled:opacity-40">Anteprima →</button>
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <p className="text-xs text-ink-400 mb-3">Anteprima con i dati del primo destinatario ({isBuyers ? `${eligible[0]?.lead.name} → ${eligible[0]?.property.title}` : `${eligible[0]?.first_name} ${eligible[0]?.last_name}`}):</p>
                        <div data-testid="composer-preview" className="border border-white/10 bg-ink-900/40 p-4 mb-5 text-sm text-ink-100 whitespace-pre-wrap">
                            {channel === "email" && <div className="text-ink-50 font-medium mb-2">📩 {isBuyers ? renderBuyerVars(subject, eligible[0]) : renderVars(subject, eligible[0])}</div>}
                            {isBuyers ? renderBuyerVars(body, eligible[0]) : renderVars(body, eligible[0])}
                            {isBuyers && channel === "email" && eligible[0]?.property.photo && <img src={eligible[0].property.photo} alt="anteprima immobile" className="mt-3 max-w-xs w-full" />}
                            {channel === "email" && <div className="mt-4 pt-3 border-t border-white/10 text-[10px] text-ink-500">{isBuyers ? "Link scheda immobile + " : ""}Non vuoi più ricevere queste comunicazioni? Disiscriviti qui. (aggiunto automaticamente)</div>}
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setStep(2)} className="cta-outline px-4 py-2.5 text-xs">← Modifica</button>
                            <button data-testid="composer-send-btn" onClick={send} disabled={sending} className="cta-gold px-5 py-2.5 text-xs flex items-center gap-2 disabled:opacity-50">
                                <Send size={12} /> {sending ? "Avvio..." : channel === "email" ? `Invia a ${eligible.length} contatti` : `Avvia sequenza WhatsApp (${eligible.length})`}
                            </button>
                        </div>
                    </>
                )}

                {step === 4 && campaign && (waMode === "automatic"
                    ? <WhatsAppAutoRunner campaign={campaign} onDone={onDone} />
                    : <WhatsAppRunner campaign={campaign} onDone={onDone} />)}

                {step === 5 && (
                    <div className="text-center py-8">
                        <CheckCircle2 size={40} className="mx-auto text-green-400 mb-4" />
                        <div data-testid="campaign-started" className="text-lg text-ink-50 font-serif mb-2">Invio email avviato</div>
                        <p className="text-xs text-ink-400 mb-4">Le email vengono inviate in background. Controlla lo stato nello Storico invii.</p>
                        <button data-testid="campaign-goto-history" onClick={onDone} className="cta-gold px-6 py-2.5 text-xs">Vai allo storico</button>
                    </div>
                )}
            </div>
            {showRisk && <RiskBanner recipientCount={eligible.length} onConfirm={confirmAuto} onClose={() => setShowRisk(false)} />}
        </div>
    );
}

function renderVars(text, c) {
    if (!c) return text;
    const full = `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Gentile proprietario";
    return (text || "").replaceAll("{nome}", c.first_name || full).replaceAll("{cognome}", c.last_name || "")
        .replaceAll("{indirizzo_immobile}", c.property_address || "il suo immobile").replaceAll("{citta}", c.city || "");
}

function renderBuyerVars(text, t) {
    if (!t) return text;
    const first = (t.lead.name || "").split(" ")[0] || "Gentile cliente";
    const p = t.property || {};
    const price = p.price ? `€ ${Number(p.price).toLocaleString("it-IT")}` : "prezzo su richiesta";
    return (text || "").replaceAll("{nome}", first)
        .replaceAll("{immobile_proposto_indirizzo}", p.address || p.city || "l'immobile")
        .replaceAll("{immobile_proposto_prezzo}", price)
        .replaceAll("{immobile_proposto_descrizione_breve}", p.title || "")
        .replaceAll("{link_scheda_immobile}", publicUrl(`/annunci/${p.property_id}`));
}

function WhatsAppRunner({ campaign, onDone }) {
    const pendingList = campaign.recipients.filter((r) => r.status === "pending");
    const [idx, setIdx] = useState(0);
    const [log, setLog] = useState({});
    const current = pendingList[idx];
    const done = idx >= pendingList.length;

    const mark = async (status) => {
        try { await api.put(`/crm/campaigns/${campaign.id}/recipients/${current.contact_id}`, { status }); } catch (e) { console.error(e); }
        setLog((l) => ({ ...l, [current.contact_id]: status }));
        setIdx((i) => i + 1);
    };
    const stop = async () => {
        try { await api.post(`/crm/campaigns/${campaign.id}/cancel`); } catch (e) { console.error(e); }
        onDone();
    };

    if (done || !current) {
        return (
            <div className="text-center py-8">
                <CheckCircle2 size={40} className="mx-auto text-green-400 mb-4" />
                <div data-testid="wa-runner-done" className="text-lg text-ink-50 font-serif mb-2">Sequenza completata</div>
                <p className="text-xs text-ink-400 mb-4">{Object.values(log).filter((s) => s === "sent").length} messaggi segnati come inviati su {pendingList.length}.</p>
                <button data-testid="wa-runner-goto-history" onClick={onDone} className="cta-gold px-6 py-2.5 text-xs">Vai allo storico</button>
            </div>
        );
    }
    return (
        <div data-testid="wa-runner">
            <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-ink-400">Contatto <b className="text-gold">{idx + 1}</b> di {pendingList.length}</div>
                <span className="text-[8px] uppercase px-1.5 py-0.5 border border-amber-600/50 text-amber-300">WhatsApp Web · Non ufficiale</span>
            </div>
            <div className="border border-green-700/30 bg-green-900/5 p-5 mb-4">
                <div className="text-sm text-ink-50 font-medium mb-1">{current.name}</div>
                <div className="text-xs text-ink-400 font-mono mb-3">{current.phone}</div>
                <div className="text-xs text-ink-100 whitespace-pre-wrap border-l-2 border-green-500/40 pl-3">{current.message}</div>
            </div>
            <div className="flex flex-wrap gap-2">
                <a data-testid="wa-open-chat" href={current.wa_link} target="_blank" rel="noreferrer" className="cta-gold px-4 py-2.5 text-xs flex items-center gap-2">
                    <ExternalLink size={12} /> Apri chat WhatsApp
                </a>
                <button data-testid="wa-mark-sent" onClick={() => mark("sent")} className="px-4 py-2.5 border border-green-700/40 text-green-300 hover:bg-green-900/20 text-xs flex items-center gap-2"><CheckCircle2 size={12} /> Inviato → prossimo</button>
                <button data-testid="wa-skip" onClick={() => mark("skipped_invalid")} className="px-4 py-2.5 border border-white/15 text-ink-300 text-xs flex items-center gap-2"><SkipForward size={12} /> Salta</button>
                <button data-testid="wa-stop" onClick={stop} className="px-4 py-2.5 border border-red-700/40 text-red-300 hover:bg-red-900/20 text-xs flex items-center gap-2"><StopCircle size={12} /> Interrompi</button>
            </div>
            <p className="text-[10px] text-ink-500 mt-3">Suggerimento: tieni WhatsApp Web già aperto e collegato in un'altra scheda. Clicca "Apri chat", premi Invia su WhatsApp, poi torna qui e segna come inviato.</p>
        </div>
    );
}

function WhatsAppAutoRunner({ campaign, onDone }) {
    const [camp, setCamp] = useState(campaign);
    const [stopping, setStopping] = useState(false);
    useEffect(() => {
        let alive = true;
        const load = () => api.get(`/crm/campaigns/${campaign.id}`).then((r) => { if (alive) setCamp(r.data); }).catch(() => {});
        const it = setInterval(load, 3000);
        return () => { alive = false; clearInterval(it); };
    }, [campaign.id]);
    const stats = camp.stats || {};
    const total = stats.total || camp.recipients.length;
    const doneCount = (stats.sent || 0) + (stats.failed || 0) + (stats.skipped || 0) + (stats.cancelled || 0);
    const finished = !!camp.completed_at || doneCount >= total;
    const stop = async () => { setStopping(true); try { await api.post(`/crm/campaigns/${campaign.id}/cancel`); } catch (e) { console.error(e); } };

    return (
        <div data-testid="wa-auto-runner">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm text-ink-50"><MessageCircle size={16} className="text-green-300" /> Invio automatico in corso</div>
                <span className="text-[8px] uppercase px-1.5 py-0.5 border border-amber-600/50 text-amber-300">WhatsApp Web · Non ufficiale</span>
            </div>
            <div className="w-full h-2 bg-ink-800 mb-4"><div className="h-2 bg-gold transition-all" style={{ width: `${total ? (doneCount / total) * 100 : 0}%` }} /></div>
            <div className="grid grid-cols-4 gap-2 mb-5 text-center text-xs">
                <div className="p-3 border border-green-700/40 bg-green-900/10"><div className="text-xl text-green-300 font-serif">{stats.sent || 0}</div><div className="text-[9px] uppercase text-ink-500">Inviati</div></div>
                <div className="p-3 border border-red-700/40 bg-red-900/10"><div className="text-xl text-red-300 font-serif">{stats.failed || 0}</div><div className="text-[9px] uppercase text-ink-500">Falliti</div></div>
                <div className="p-3 border border-amber-700/40 bg-amber-900/10"><div className="text-xl text-amber-300 font-serif">{stats.pending || 0}</div><div className="text-[9px] uppercase text-ink-500">In coda</div></div>
                <div className="p-3 border border-orange-700/40 bg-orange-900/10"><div className="text-xl text-orange-300 font-serif">{stats.skipped || 0}</div><div className="text-[9px] uppercase text-ink-500">Saltati</div></div>
            </div>
            {finished ? (
                <div className="text-center">
                    <CheckCircle2 size={32} className="mx-auto text-green-400 mb-2" />
                    <div data-testid="wa-auto-done" className="text-ink-50 font-serif mb-3">Invio automatico completato</div>
                    <button onClick={onDone} className="cta-gold px-6 py-2.5 text-xs">Vai allo storico</button>
                </div>
            ) : (
                <>
                    <p className="text-[11px] text-ink-400 mb-3 flex items-center gap-2"><Loader2 size={12} className="animate-spin text-gold" /> Invio in sequenza con ritardo casuale 5-15s tra un messaggio e l&apos;altro. Tieni questa scheda aperta.</p>
                    <button data-testid="wa-auto-stop" onClick={stop} disabled={stopping} className="px-4 py-2.5 border border-red-700/40 text-red-300 hover:bg-red-900/20 text-xs flex items-center gap-2"><StopCircle size={12} /> {stopping ? "Interruzione..." : "Interrompi invio"}</button>
                </>
            )}
        </div>
    );
}

function RiskBanner({ recipientCount, onConfirm, onClose }) {
    const [ack, setAck] = useState(false);
    const highVolume = recipientCount > 50;
    return (
        <div className="fixed inset-0 z-[10070] bg-black/85 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div data-testid="wa-risk-banner" className="bg-ink-950 border-2 border-amber-600/60 max-w-xl w-full max-h-[85vh] overflow-y-auto p-6">
                <div className="flex items-center gap-2 text-amber-300 mb-4"><AlertTriangle size={20} /><h4 className="font-serif text-xl">Avviso rischi — Invio automatico WhatsApp</h4></div>
                {highVolume && <div className="mb-4 p-3 border border-red-600/50 bg-red-900/15 text-[11px] text-red-200"><b>Attenzione:</b> stai per inviare a <b>{recipientCount}</b> contatti in un solo invio. Volumi elevati aumentano molto il rischio di ban. Valuta la Modalità Semi-Manuale.</div>}
                <ul className="text-xs text-ink-200 space-y-2.5 mb-5 list-disc pl-4">
                    <li>L&apos;invio automatico via WhatsApp Web tramite strumenti non ufficiali <b className="text-amber-300">VIOLA i Termini di Servizio di WhatsApp/Meta</b>.</li>
                    <li>Rischio concreto di <b className="text-red-300">BAN temporaneo o definitivo</b> del numero di telefono dell&apos;agenzia, con perdita di tutte le chat e i contatti collegati.</li>
                    <li>Il rischio aumenta con: alto volume di messaggi in poco tempo, numero nuovo o poco &quot;riscaldato&quot;, destinatari che bloccano o segnalano come spam, messaggi identici inviati in rapida successione.</li>
                    <li>Consigliato usare un <b>numero secondario/dedicato</b> per i test iniziali, non il numero principale dell&apos;agenzia.</li>
                    <li>Per volumi alti o invii importanti, preferisci la <b>Modalità Semi-Manuale</b>.</li>
                    <li>Il CRM non si assume responsabilità per il blocco del numero: la scelta è a discrezione e rischio dell&apos;agenzia.</li>
                </ul>
                <label className="flex items-center gap-2 text-xs text-ink-100 mb-4 cursor-pointer">
                    <input data-testid="risk-ack-check" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> Ho capito i rischi
                </label>
                <div className="flex gap-2">
                    <button data-testid="risk-cancel" onClick={onClose} className="cta-outline px-4 py-2.5 text-xs">Annulla</button>
                    <button data-testid="risk-confirm" onClick={onConfirm} disabled={!ack} className="cta-gold px-5 py-2.5 text-xs disabled:opacity-40">Procedo comunque</button>
                </div>
            </div>
        </div>
    );
}
